import React from 'react';
import { Box, Breadcrumbs, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getColors } from '../theme/tokens';

export interface Crumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  /** Breadcrumb trail rendered above the title; last crumb is the current page. */
  crumbs?: Crumb[];
  /** Smaller text under the title. */
  subtitle?: React.ReactNode;
  /** Right-aligned actions. */
  actions?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, crumbs, subtitle, actions }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);

  return (
    <Box sx={{ mb: 3 }}>
      {crumbs && crumbs.length > 0 && (
        <Breadcrumbs
          separator="›"
          sx={{ mb: 0.75, '& .MuiBreadcrumbs-separator': { color: c.inkFaint } }}
        >
          {crumbs.map((crumb, i) =>
            crumb.to ? (
              <MuiLink
                key={i}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                sx={{ color: c.inkMuted, fontSize: '0.85rem', fontWeight: 500 }}
              >
                {crumb.label}
              </MuiLink>
            ) : (
              <Typography key={i} sx={{ color: c.inkSecondary, fontSize: '0.85rem', fontWeight: 600 }}>
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: '1.625rem',
              fontWeight: 700,
              letterSpacing: '-0.625px',
              lineHeight: 1.23,
              color: c.ink,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: c.inkMuted, mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
      </Box>
    </Box>
  );
};

export default PageHeader;
